import { createContext, useContext, useState, useCallback, ReactNode, useMemo } from 'react'

interface AssistantEditContextType {
  editedAssistant: AssistantVersion | null
  originalAssistant: AssistantVersion | null
  hasChanges: boolean
  hasUnsavedExtractions: boolean
  logoFile: File | null
  updateField: (field: string, value: string) => void
  updateKnowledgeBase: (knowledgeBase: Array<{
    folderId: string
    folderName: string
    usageInstructions: string
  }>) => void
  updatePreferences: (preferences: PreferenceDTO) => void
  updateIntegrations: (integrations: IntegrationDTO) => void
  updateTools: (tools: AssistantToolsDTO) => void
  setLogoFile: (file: File | null) => void
  resetToOriginal: () => void
  setOriginalAssistant: (assistant: AssistantVersion | null) => void
  setManualSelection: (isManual: boolean) => void
  isManualSelection: boolean
}

const AssistantEditContext = createContext<AssistantEditContextType | null>(null)

export const useAssistantEdit = () => {
  const context = useContext(AssistantEditContext)
  if (!context) {
    throw new Error('useAssistantEdit must be used within AssistantEditProvider')
  }
  return context
}

interface AssistantEditProviderProps {
  children: ReactNode
}

export const AssistantEditProvider = ({ children }: AssistantEditProviderProps) => {
  const [editedAssistant, setEditedAssistant] = useState<AssistantVersion | null>(null)
  const [original, setOriginal] = useState<AssistantVersion | null>(null)
  const [isManualSelection, setIsManualSelection] = useState(false)
  const [logoFile, setLogoFile] = useState<File | null>(null)

  const setOriginalAssistant = useCallback((assistant: AssistantVersion | null) => {
    setOriginal(assistant)
    setEditedAssistant(assistant)
    setLogoFile(null)
  }, [])

  const setManualSelection = useCallback((isManual: boolean) => {
    setIsManualSelection(isManual)
  }, [])

  const updateField = useCallback((field: string, value: string) => {
    setEditedAssistant(prev => {
      if (!prev) return prev
      return {
        ...prev,
        [field]: value
      }
    })
  }, [])

  const updateKnowledgeBase = useCallback((knowledgeBase: Array<{
    folderId: string
    folderName: string
    usageInstructions: string
  }>) => {
    setEditedAssistant(prev => {
      if (!prev) return prev
      return {
        ...prev,
        knowledge_base: knowledgeBase.map(kb => ({
          folder_id: kb.folderId,
          usage_instructions: kb.usageInstructions,
        }))
      }
    })
  }, [])

  const updatePreferences = useCallback((preferences: PreferenceDTO) => {
    setEditedAssistant(prev => {
      if (!prev) return prev
      return {
        ...prev,
        preferences
      }
    })
  }, [])

  const updateIntegrations = useCallback((integrations: IntegrationDTO) => {
    setEditedAssistant(prev => {
      if (!prev) return prev
      return {
        ...prev,
        integrations
      }
    })
  }, [])

  const updateTools = useCallback((tools: AssistantToolsDTO) => {
    setEditedAssistant(prev => {
      if (!prev) return prev
      return {
        ...prev,
        tools
      }
    })
  }, [])

  const resetToOriginal = useCallback(() => {
    setEditedAssistant(original)
    setLogoFile(null)
  }, [original])

  const hasChanges = useMemo(() => {
    if (!original || !editedAssistant) return false
    if (original.name !== editedAssistant.name) return true
    if (original.description !== editedAssistant.description) return true
    
    const originalKb = original.knowledge_base || []
    const editedKb = editedAssistant.knowledge_base || []
    if (originalKb.length !== editedKb.length) return true
    
    for (let i = 0; i < originalKb.length; i++) {
      const orig = originalKb[i]
      const edit = editedKb[i]
      if (orig.folder_id !== edit.folder_id || orig.usage_instructions !== edit.usage_instructions) {
        return true
      }
    }
    
    if (JSON.stringify(original.preferences) !== JSON.stringify(editedAssistant.preferences)) {
      return true
    }   
    if (JSON.stringify(original.integrations) !== JSON.stringify(editedAssistant.integrations)) {
      return true
    }
    if (JSON.stringify(original.tools) !== JSON.stringify(editedAssistant.tools)) {
      return true
    }
    
    if (logoFile) return true
    
    return false
  }, [original, editedAssistant, logoFile])

  // Check if there are unsaved extractions
  const hasUnsavedExtractions = useMemo(() => {
    if (!original || !editedAssistant) return false
    
    const originalExtractions = original.tools?.extractions || []
    const editedExtractions = editedAssistant.tools?.extractions || []
    
    // Different number of extractions
    if (originalExtractions.length !== editedExtractions.length) return true
    
    // Check if any extraction has been modified
    return editedExtractions.some((edited, index) => {
      const original = originalExtractions[index]
      return JSON.stringify(original) !== JSON.stringify(edited)
    })
  }, [original, editedAssistant])

  const value: AssistantEditContextType = useMemo(() => ({
    editedAssistant,
    originalAssistant: original,
    hasChanges,
    hasUnsavedExtractions,
    logoFile,
    updateField,
    updateKnowledgeBase,
    updatePreferences,
    updateIntegrations,
    updateTools,
    setLogoFile,
    resetToOriginal,
    setOriginalAssistant,
    setManualSelection,
    isManualSelection,
  }), [
    editedAssistant,
    original,
    hasChanges,
    hasUnsavedExtractions,
    logoFile,
    updateField,
    updateKnowledgeBase,
    updatePreferences,
    updateIntegrations,
    updateTools,
    setLogoFile,
    resetToOriginal,
    setOriginalAssistant,
    setManualSelection,
    isManualSelection,
  ])

  return (
    <AssistantEditContext.Provider value={value}>
      {children}
    </AssistantEditContext.Provider>
  )
} 